import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useDragDismiss } from '../ui/drag.js';
import { useSheetLayer } from '../ui/OverlayHost.js';
import { mono } from '../ui/atoms.js';

/** Tells the sheet a game is under way (its START pressed); see `useGameUnderWay`. */
const UnderWay = createContext<((playing: boolean) => void) | null>(null);

/**
 * A game is being played in this sheet: on a short screen the sheet folds its instruction line away
 * (the how-to card has just shown the move), and the board has that much more room (styles.css).
 */
export function useGameUnderWay(playing: boolean): void {
  const set = useContext(UnderWay);
  useEffect(() => {
    set?.(playing);
    return () => set?.(false);
  }, [set, playing]);
}

/**
 * A game, full screen, over everything (the header and the toolbar too, so a small phone has
 * room for the board and its buttons). Alarms and messages still drop in over it.
 *
 * It closes like an iOS sheet, but only from its header: a grabber and a 44-point ✕ at the
 * top, and a pull down on the header. The board below keeps every gesture for the game.
 */
export function GameScreen({
  accent,
  eyebrow,
  title,
  instruction,
  closeLabel,
  onClose,
  children,
}: {
  accent: string;
  eyebrow: string;
  title: string;
  instruction: string;
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const drag = useDragDismiss<HTMLDivElement>({ direction: 'down', onDismiss: onClose, fraction: 0.25 });
  const layer = useSheetLayer();
  const [playing, setPlaying] = useState(false);

  const screen = (
    <div
      ref={drag.ref}
      role="dialog"
      aria-label={title}
      className={playing ? 'cc-sheet playing' : 'cc-sheet'}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 30,
        display: 'flex',
        flexDirection: 'column',
        background: '#05070b',
        pointerEvents: 'auto',
        // Below whatever banner is showing (BannerHost sets --banner-h), never under it.
        paddingTop: 'calc(env(safe-area-inset-top) + var(--banner-h, 0px))',
        transition: 'padding-top .25s ease',
        paddingBottom: 'env(safe-area-inset-bottom)',
        boxSizing: 'border-box',
      }}
    >
      <div
        ref={drag.handleRef}
        {...drag.bind}
        style={{ flex: 'none', padding: '0 12px 10px 20px', touchAction: 'none', borderBottom: '1px solid rgba(255,255,255,.08)' }}
      >
        <div style={{ height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span aria-hidden="true" style={{ width: 36, height: 5, borderRadius: 3, background: accent, opacity: 0.5 }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
            <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 1.8, color: accent }}>{eyebrow}</div>
            <h2 style={{ margin: '6px 0 4px', fontSize: 22, fontWeight: 700, letterSpacing: 1, lineHeight: 1.15 }}>{title}</h2>
          </div>
          <button
            onClick={drag.dismiss}
            aria-label={closeLabel}
            style={{
              flex: 'none',
              width: 44,
              height: 44,
              borderRadius: 22,
              border: '1px solid rgba(255,255,255,.2)',
              background: 'rgba(255,255,255,.06)',
              color: '#dfe9f2',
              fontSize: 18,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
        <div className="cc-sheet-how" style={{ fontFamily: mono, fontSize: 13, color: '#9db3c2', lineHeight: 1.5 }}>
          {instruction}
        </div>
      </div>
      <div
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          padding: '4px 20px 20px',
        }}
      >
        <UnderWay.Provider value={setPlaying}>{children}</UnderWay.Provider>
      </div>
    </div>
  );
  return layer ? createPortal(screen, layer) : screen;
}
